import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, X, Copy } from "lucide-react";

export const DAY_LABELS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
export const DAY_SHORT = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
export const emptySchedule = () => ({ days: {}, note: "" });

// Admin editor: weekly schedule with several blocks per day (split shifts).
export default function DriverScheduleEditor({ value, onChange }) {
  const sched = value || emptySchedule();
  const blocksOf = (d) => sched.days?.[String(d)] || [];
  const setBlocks = (d, blocks) => onChange({ ...sched, days: { ...(sched.days || {}), [String(d)]: blocks } });
  const addBlock = (d) => {
    const prev = blocksOf(d);
    const def = prev.length === 0 ? { start: "07:00", end: "12:00" } : { start: "15:00", end: "20:00" };
    setBlocks(d, [...prev, def]);
  };
  const updBlock = (d, i, k, v) => setBlocks(d, blocksOf(d).map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const delBlock = (d, i) => setBlocks(d, blocksOf(d).filter((_, j) => j !== i));
  const copyMonToFri = () => {
    const mon = blocksOf(0);
    const days = { ...(sched.days || {}) };
    [1, 2, 3, 4].forEach((d) => { days[String(d)] = mon.map((b) => ({ ...b })); });
    onChange({ ...sched, days });
  };

  return (
    <div className="space-y-2" data-testid="driver-schedule-editor">
      <div className="flex items-center justify-between">
        <Label>Horario del chofer</Label>
        <button
          type="button" onClick={copyMonToFri} data-testid="schedule-copy-week"
          className="text-[10px] font-mono uppercase tracking-widest text-primary hover:text-primary/80 flex items-center gap-1"
        >
          <Copy className="w-3 h-3" /> Copiar lunes a Mar–Vie
        </button>
      </div>
      <div className="rounded-md border border-border divide-y divide-border">
        {DAY_LABELS.map((label, d) => {
          const blocks = blocksOf(d);
          return (
            <div key={d} className="flex items-start gap-2 px-2 py-1.5" data-testid={`schedule-day-${d}`}>
              <div className={`w-16 shrink-0 text-xs font-bold pt-1.5 ${blocks.length ? "text-white" : "text-muted-foreground"}`}>{label}</div>
              <div className="flex-1 flex flex-wrap gap-1.5">
                {blocks.length === 0 && <span className="text-[11px] text-muted-foreground pt-1.5 italic">Libre</span>}
                {blocks.map((b, i) => (
                  <div key={i} className="flex items-center gap-1 rounded bg-input/60 border border-border px-1" data-testid={`schedule-block-${d}-${i}`}>
                    <Input type="time" value={b.start} onChange={(e) => updBlock(d, i, "start", e.target.value)}
                      className="h-7 w-[92px] px-1 bg-transparent border-0 font-mono text-xs" data-testid={`schedule-start-${d}-${i}`} />
                    <span className="text-muted-foreground text-xs">–</span>
                    <Input type="time" value={b.end} onChange={(e) => updBlock(d, i, "end", e.target.value)}
                      className="h-7 w-[92px] px-1 bg-transparent border-0 font-mono text-xs" data-testid={`schedule-end-${d}-${i}`} />
                    <button type="button" onClick={() => delBlock(d, i)} className="text-muted-foreground hover:text-red-400" data-testid={`schedule-del-${d}-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => addBlock(d)} disabled={blocks.length >= 4}
                className="h-7 px-2 border-border text-[11px] shrink-0" data-testid={`schedule-add-${d}`}>
                <Plus className="w-3 h-3 mr-0.5" /> Turno
              </Button>
            </div>
          );
        })}
      </div>
      <Input
        value={sched.note || ""} onChange={(e) => onChange({ ...sched, note: e.target.value })}
        placeholder="Nota para el chofer (opcional): ej. DOMINGOS SOLO SI HAY DEMANDA"
        className="bg-input border-border text-xs" data-testid="schedule-note"
      />
      <div className="text-[11px] text-muted-foreground">Varios turnos en un mismo día = turno cortado (mañana y tarde). El chofer lo ve tocando el logo en su app.</div>
    </div>
  );
}
